import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";

const CATEGORIES = [
  { v: "logo", label: "Logo" },
  { v: "produit", label: "Photos produit" },
  { v: "lifestyle", label: "Lifestyle" },
  { v: "jeu", label: "Photos du jeu" },
  { v: "b2b", label: "B2B" },
  { v: "point-de-vente", label: "Points de vente" },
  { v: "actualites", label: "Actualités" },
  { v: "application", label: "Application" },
  { v: "autre", label: "Autres" },
];

export default function MediaAdmin() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState("");
  const [uploadCat, setUploadCat] = useState("produit");
  const [filter, setFilter] = useState("tous");
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name);
      fd.append("category", uploadCat);
      await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image téléversée et optimisée");
      load();
    } catch (err) {
      toast.error(apiError(err, "Téléversement impossible"));
    } finally {
      setUploading(false);
    }
  };

  const update = async (item, patch) => {
    try {
      await api.put(`/admin/media/${item.id}`, { alt: item.alt, category: item.category || "autre", ...patch });
      toast.success("Enregistré");
      load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette image ? Les contenus qui l'utilisent afficheront une image vide.")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Image supprimée");
    load();
  };

  const copy = (url, id) => {
    navigator.clipboard?.writeText(mediaUrl(url));
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const filtered = items ? items.filter((m) => filter === "tous" || (m.category || "autre") === filter) : [];

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Médiathèque</h1>
          <p className="text-sm text-muted-foreground mt-1">Les images sont automatiquement redimensionnées et optimisées pour le web.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="media-upload-category"
            className="border border-input bg-white px-3 py-2.5 text-sm">
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-button"
            className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? "Téléversement…" : "Téléverser"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="media-file-input" onChange={upload} />
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-6" data-testid="media-filters">
        {[{ v: "tous", label: "Tous" }, ...CATEGORIES].map((c) => (
          <button key={c.v} onClick={() => setFilter(c.v)} data-testid={`media-filter-${c.v}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${filter === c.v ? "bg-onyx text-cream border-onyx" : "bg-white border-line text-onyx hover:border-rouge"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {!items ? <p className="text-muted-foreground">Chargement…</p> : filtered.length === 0 ? (
        <div className="bg-white border border-line p-10 text-center" data-testid="media-empty">
          <p className="text-sm text-muted-foreground">Aucune image dans cette catégorie.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {filtered.map((m) => (
            <div key={m.id} className="bg-white border border-line" data-testid={`media-item-${m.id}`}>
              <div className="aspect-square bg-sand flex items-center justify-center p-3">
                <img src={mediaUrl(m.url)} alt={m.alt} loading="lazy" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="p-3 space-y-2">
                <input defaultValue={m.alt} onBlur={(e) => e.target.value !== m.alt && update(m, { alt: e.target.value })}
                  data-testid={`media-alt-${m.id}`} aria-label="Texte alternatif" placeholder="Texte alternatif (alt)"
                  className="w-full text-xs border border-input px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-rouge" />
                <select value={m.category || "autre"} onChange={(e) => update(m, { category: e.target.value })}
                  data-testid={`media-category-${m.id}`} aria-label="Catégorie"
                  className="w-full text-xs border border-input bg-white px-2 py-1.5">
                  {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
                <div className="flex items-center justify-between">
                  <button onClick={() => copy(m.url, m.id)} data-testid={`media-copy-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-onyx hover:text-rouge">
                    {copied === m.id ? <Check className="w-3.5 h-3.5 text-field" /> : <Copy className="w-3.5 h-3.5" />} URL
                  </button>
                  <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-onyx hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
